import React from 'react';
import { BCorpLogo } from '@/components/svgs/BCorpLogo';
import { PrimaryProofPointProps, SecondaryProofPointProps } from '@/components/WhyAthena/WhyAthena';
import { AthenaLogo } from '@/components/svgs/AthenaLogo';
import { SuperScript } from '@/components/cohort/SuperScript';

export const WHY_ATHENA_REFINANCING_CONTENT: Array<PrimaryProofPointProps> = [
  {
    title: 'Fiercely fair value',
    paragraph: (
      <>
        Changing home loans for good is in our DNA and can save you money. So far we&apos;ve saved thousands of Aussies
        a total of $704m
        <SuperScript>**</SuperScript> and counting.
      </>
    ),
    buttonText: 'More about fiercely fair value',
    modalContent: [
      {
        title: <h3>Aussie-firsts on Straight Up and Power Up loans</h3>,
      },
      {
        title: 'No Athena fees',
        body: 'No application fees. No ongoing fees. No discharge fees. Zero. Zilch. Nada.',
      },
      {
        title: 'Drop your own rate with AcceleRATES',
        body: (
          <>
            We&apos;ll automatically drop your rate as you pay down your home loan
            <SuperScript>2</SuperScript>.
          </>
        ),
      },
      {
        title: 'No loyalty tax',
        body: (
          <>
            We always give existing and new customers the same sexy rates for like-for-like loans
            <SuperScript>3</SuperScript>. We&apos;ll have your back.
          </>
        ),
      },
    ],
    imageAlt: 'Customer Savings 60k',
    imageSrcDesktop: '/next-assets/images/cohort/why-cust-savings-52k-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-cust-savings-52k-sm.png',
  },
  {
    title: 'Get max borrowing power',
    paragraph:
      'Grab the investment you really want by maximising your borrowing power with Athena. We get you fully credit pre-approved in days, and you can make changes to your Straight Up or Power Up loan without being charged.',
    buttonText: 'More borrowing power less fees',
    modalContent: [
      {
        title: 'Grab the investment you really want',
        body: 'Get maximum borrowing power with our investor friendly policies. We make the numbers work, so you get the place your heart is set on. See your personalised borrowing power in just 3 minutes.',
      },
      {
        title: 'Get fast and accurate pre-approval',
        body: 'Bid with confidence knowing you have accurate, fully assessed pre-approval. Plus it lasts for 90 days!',
      },
      {
        title: 'Get flexibility without the fees',
        body: 'Easily switch to Interest Only, change your repayment frequency or update your repayment method. And changes to your Straight Up or Power Up loan have no charges.',
      },
    ],
    imageAlt: 'Borrowing Amount',
    imageSrcDesktop: '/next-assets/images/cohort/why-borrow-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-borrow-sm.png',
  },
  {
    title: 'Offset and save',
    paragraph:
      "With Athena's offset accounts, you can stash your cash any way you want on our Power Up loan. The more money you put in your offset, the less interest you'll pay.",
    buttonText: 'More about offsetting your interest',
    modalContent: [
      {
        title: 'Offset and save',
        body: (
          <>
            Set up your offsets (up to 10) and stash your cash to reduce the interest payable on your loan. You only pay
            interest based on the net loan balance after taking away the total of your offset balances attached to that
            loan.
            <br />
            <br />
            Salary credit into your offset account to maximise your interest savings.
            <br />
            <br />
            Set and forget with scheduled payments to your transaction or credit card accounts.
          </>
        ),
      },
      {
        title: 'Investor tips',
        body: (
          <>
            <strong>Boost your earnings</strong> with an offset on your owner occupied loans and stash rental income in
            a separate Cash Stash offset account. You&apos;ll reduce interest on your loan while keeping funds available
            for investment expenses.
            <br />
            <br />
            <strong>Unlock the power of equity.</strong> For future investments, add funds to your offset account and
            ensure their availability for your next move. For optimal tax benefits, seek advice from your tax advisor.
          </>
        ),
      },
    ],
    imageAlt: 'List of offset accounts',
    imageSrcDesktop: '/next-assets/images/cohort/why-offsets-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-offsets-sm.png',
  },
  {
    title: 'Optimise your loan structure',
    paragraph:
      'Split up to 10 loans against your property. Each split loan is customisable — choose fixed or variable, owner occupier or investment, P&I or IO and set different loan terms.',
    buttonText: 'More about our powerful investor toolkit',
    modalContent: [
      {
        title: 'Split for tax purposes',
        body: "If you have an owner occupied property with equity available, you could split the loan against it to access equity for investment purposes. Maximise your tax deductions on the investment loan with IO repayments and pay down that owner occupied loan sooner with P&I repayments. Remember we're not able to give you tax advice so make sure you speak to your professional adviser about the right strategy for you!",
      },
      {
        title: 'Split it for debt recycling',
        body: 'Do you have extra repayments in an offset against your owner occupied loan and you want to use the amount for investment purposes? Split it with our Limit Transfer feature to support your debt recycling strategy. A limit transfer allows you to increase your investor loan limit and transfer the offset funds to this loan while reducing your owner occupied loan limit. Limit transfers could help structure your loan for tax purposes as the interest charged on the investment loan could be tax-deductible.',
      },
    ],
    imageAlt: 'Split your loan',
    imageSrcDesktop: '/next-assets/images/cohort/why-splits-lg.svg',
    imageSrcMobile: '/next-assets/images/cohort/why-splits-sm.svg',
  },
  {
    title: 'Investor Experts who get you',
    paragraph: (
      <>
        Our Experts understand investors. Talk to them about unlocking equity, structuring your loans or maximising your
        borrowing power
        <SuperScript>6</SuperScript>. They&apos;re on-hand to help you win and are just an SMS or call away.
      </>
    ),
    buttonText: '',
    modalContent: null,
    imageAlt: "Athena's Loan Ranger team",
    imageSrcDesktop: '/next-assets/images/cohort/why-rangers-sitting-lg.png',
    imageSrcMobile: '/next-assets/images/cohort/why-rangers-sitting-lg.png',
  },
  {
    title: 'Self-employed or need a lower deposit loan?',
    paragraph: (
      <>
        Our Tailored loans are custom-made for you. Our lite doc loan makes it easy for self-employed borrowers who need
        alternative income verification options. For borrowers with a lower deposit, we’ve got you covered with our
        80-85% LVR loan, no LMI needed. And we cater for investors who are buying property through a company or trust.
      </>
    ),
    buttonText: 'More about our Tailored loans',
    buttonLink: '/tailored',
    modalContent: null,
    imageAlt: 'Search',
    imageSrcDesktop: '/next-assets/images/cohort/tailored-cohort-image.svg',
    imageSrcMobile: '/next-assets/images/cohort/tailored-cohort-image.svg',
  },
];

export const WHY_ATHENA_SECONDARY_PROOF_POINTS: Array<SecondaryProofPointProps> = [
  {
    image: <BCorpLogo />,
    title: 'Doing business for good',
    body: "Balancing profit with people and purpose, Athena is a certified B-Corp. Proof we're committed to a better, fairer world. B-Corps are businesses that meet high standards of social and environmental performance, accountability, and transparency.",
  },
  {
    image: <AthenaLogo />,
    title: 'The home loan wrecker',
    body: "Since our launch in 2019 we've settled over $6.3 billion in loans for Aussies. Home loans are all we do, and we're obsessed with changing them for good. We continue to rewrite the rules, creating tools, products and policy that put the power back in our customers' hands.",
  },
];
